import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  DateField,
  Field,
  Modal,
  ModalActions,
  SearchSelect,
  TextArea,
} from '@evogard/ui';
import type {
  PeripheralItem,
  PeripheralItemWrite,
  PeripheralQuote,
  PeripheralQuoteWrite,
} from '@evogard/contract';
import {
  PERIPHERAL_AVAILABILITIES,
  PERIPHERAL_INVOICING_TYPES,
  PERIPHERAL_ITEM_TYPES,
  PERIPHERAL_PART_ORIGINS,
  PERIPHERAL_QUOTE_ORIGINS,
  fromColumn,
  moneyInputText,
  parseMoneyInput,
  peripheralItemQuestions,
  toColumn,
  type PeripheralItemType,
} from '@evogard/domain';
import { api } from '../../../lib/api.js';
import { useSessionContext } from '../../../lib/session.js';

const optionsOf = (o: Record<string, string>) =>
  Object.entries(o).map(([value, label]) => ({ value, label }));
const YES_NO = [
  { value: 'sim', label: 'Sim' },
  { value: 'nao', label: 'Não' },
];
const fromYesNo = (v: string) => (v === '' ? null : v === 'sim');
const toYesNo = (v: boolean | null | undefined) => (v == null ? '' : v ? 'sim' : 'nao');

/**
 * Acionar ou corrigir um item: o tipo da peça escolhe o questionário (pipe
 * "[PER] Itens"). Trocar o tipo troca as perguntas, e a API apaga as
 * respostas da família anterior.
 */
export function ItemFormModal({
  item,
  busy,
  error,
  onClose,
  onSubmit,
}: {
  item: PeripheralItem | null;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (body: PeripheralItemWrite) => void;
}) {
  const [itemType, setItemType] = useState(item?.itemType ?? '');
  const [damage, setDamage] = useState(toYesNo(item?.preExistingDamage));
  const [notes, setNotes] = useState(item?.notes ?? '');
  const [answers, setAnswers] = useState<Record<string, string | null>>(
    item?.answers ?? {},
  );
  const questions = itemType
    ? peripheralItemQuestions(itemType as PeripheralItemType)
    : [];

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title={item ? 'Corrigir o item' : 'Acionar item'}
      descricao="A peça e o questionário técnico dela — as respostas que a oficina e o fornecedor precisam."
    >
      <div className="flex flex-col gap-4">
        {error && <Alert tone="erro">{error}</Alert>}
        <SearchSelect
          label="Tipo periférico"
          required
          options={optionsOf(PERIPHERAL_ITEM_TYPES)}
          value={itemType}
          onChange={(v) => setItemType(v)}
        />
        <SearchSelect
          label="Avaria pré-existente?"
          options={YES_NO}
          value={damage}
          onChange={setDamage}
          clearable
        />
        {questions.map((q) => (
          <SearchSelect
            key={q.column}
            label={q.label}
            options={q.options}
            value={answers[q.column] ?? ''}
            onChange={(v) =>
              setAnswers((a) => ({ ...a, [q.column]: v === '' ? null : v }))
            }
            clearable
          />
        ))}
        {itemType && questions.length === 0 && (
          <p className="text-sm text-texto-suave">
            Este tipo de peça não tem questionário.
          </p>
        )}
        <TextArea
          label="Observações"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={4000}
        />
        <ModalActions>
          <Button variant="secundario" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            disabled={itemType === ''}
            loading={busy}
            onClick={() =>
              onSubmit({
                itemType,
                preExistingDamage: fromYesNo(damage),
                notes: notes.trim() === '' ? null : notes.trim(),
                /* Só as perguntas do tipo escolhido — as da família anterior, a API apaga. */
                answers: Object.fromEntries(
                  questions.map((q) => [q.column, answers[q.column] ?? null]),
                ),
              })
            }
          >
            {item ? 'Salvar o item' : 'Acionar'}
          </Button>
        </ModalActions>
      </div>
    </Modal>
  );
}

/** Os prestadores da base — quem cota a peça. */
function useProviders() {
  const { data: context } = useSessionContext();
  return useQuery({
    queryKey: ['sinistros', 'perifericos', 'prestadores', context?.currentTenant.id],
    queryFn: async () => {
      const r = await api.claims.listCompanies({
        query: { category: 'prestadores', page: 1, perPage: 200 },
      });
      if (r.status !== 200) throw new Error('Não foi possível carregar os prestadores.');
      return r.body.items;
    },
    enabled: context !== undefined,
  });
}

/**
 * Cadastrar ou corrigir uma cotação. O valor chega depois do pedido: sem ele,
 * a cotação aguarda valores; com ele, aguarda aprovação. Aprovar não é daqui
 * — é do supervisor, na gaveta, na Aprovação do Supervisor.
 */
export function QuoteFormModal({
  quote,
  busy,
  error,
  onClose,
  onSubmit,
}: {
  quote: PeripheralQuote | null;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (body: PeripheralQuoteWrite) => void;
}) {
  const providers = useProviders();
  const initialValue = fromColumn(quote?.quotedValue ?? null);
  const [provider, setProvider] = useState(
    quote?.providerCompanyId ? String(quote.providerCompanyId) : '',
  );
  const [vendorName, setVendorName] = useState(quote?.vendorName ?? '');
  const [vendorPhone, setVendorPhone] = useState(quote?.vendorPhone ?? '');
  const [value, setValue] = useState(
    initialValue === null ? '' : moneyInputText(initialValue),
  );
  const [brand, setBrand] = useState(quote?.brand ?? '');
  const [partOrigin, setPartOrigin] = useState(quote?.partOrigin ?? '');
  const [quoteOrigin, setQuoteOrigin] = useState(quote?.quoteOrigin ?? '');
  const [preference, setPreference] = useState(toYesNo(quote?.purchasePreference));
  const [invoicing, setInvoicing] = useState(quote?.invoicingType ?? '');
  const [availability, setAvailability] = useState(quote?.availability ?? '');
  const [arrival, setArrival] = useState(quote?.expectedArrival ?? '');
  const parsed = value.trim() === '' ? null : parseMoneyInput(value);
  const invalidValue = value.trim() !== '' && parsed === null;
  const orNull = (v: string) => (v.trim() === '' ? null : v.trim());

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title={quote ? 'Corrigir a cotação' : 'Nova cotação'}
      descricao="Quem cotou, o valor e as condições da peça."
    >
      <div className="flex flex-col gap-4">
        {error && <Alert tone="erro">{error}</Alert>}
        {providers.isError && <Alert tone="erro">{providers.error.message}</Alert>}
        <SearchSelect
          label="Prestador de Serviço"
          options={(providers.data ?? []).map((c) => ({
            value: String(c.id),
            label: c.name,
          }))}
          value={provider}
          onChange={setProvider}
          clearable
          sortable
          loading={providers.isLoading}
          help={
            providers.data?.length === 0
              ? 'Nenhum prestador cadastrado nesta base. Cadastre em Sinistros › Prestadores.'
              : undefined
          }
        />
        <Field
          label="Nome do Vendedor"
          value={vendorName}
          onChange={(e) => setVendorName(e.target.value)}
          maxLength={200}
        />
        <Field
          label="Telefone Vendedor"
          inputMode="tel"
          value={vendorPhone}
          onChange={(e) => setVendorPhone(e.target.value)}
          maxLength={40}
        />
        <Field
          label="Valor da cotação"
          prefix="R$"
          mask="money"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          error={invalidValue ? 'Um valor em reais, como 1.234,56.' : null}
          help="Sem valor, a cotação fica aguardando valores."
        />
        <Field
          label="Marca do produto/serviço"
          value={brand}
          onChange={(e) => setBrand(e.target.value)}
          maxLength={200}
        />
        <SearchSelect
          label="Origem da peça"
          options={optionsOf(PERIPHERAL_PART_ORIGINS)}
          value={partOrigin}
          onChange={setPartOrigin}
          clearable
        />
        <SearchSelect
          label="Origem da cotação"
          options={optionsOf(PERIPHERAL_QUOTE_ORIGINS)}
          value={quoteOrigin}
          onChange={setQuoteOrigin}
          clearable
        />
        <SearchSelect
          label="Preferência de compra"
          options={YES_NO}
          value={preference}
          onChange={setPreference}
          clearable
        />
        <SearchSelect
          label="Tipo de Faturamento"
          options={optionsOf(PERIPHERAL_INVOICING_TYPES)}
          value={invoicing}
          onChange={setInvoicing}
          clearable
        />
        <SearchSelect
          label="Disponibilidade da peça"
          options={optionsOf(PERIPHERAL_AVAILABILITIES)}
          value={availability}
          onChange={setAvailability}
          clearable
        />
        <DateField
          label="Previsão de Chegada"
          layer="modal"
          value={arrival}
          onChange={setArrival}
        />
        <ModalActions>
          <Button variant="secundario" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            disabled={invalidValue}
            loading={busy}
            onClick={() =>
              onSubmit({
                providerCompanyId: provider === '' ? null : Number(provider),
                vendorName: orNull(vendorName),
                vendorPhone: orNull(vendorPhone),
                quotedValue: parsed === null ? null : toColumn(parsed),
                brand: orNull(brand),
                partOrigin: orNull(partOrigin),
                quoteOrigin: orNull(quoteOrigin),
                purchasePreference: fromYesNo(preference),
                invoicingType: orNull(invoicing),
                availability: orNull(availability),
                expectedArrival: orNull(arrival),
              })
            }
          >
            {quote ? 'Salvar a cotação' : 'Cadastrar'}
          </Button>
        </ModalActions>
      </div>
    </Modal>
  );
}
